import React from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import SVG from 'react-inlinesvg';
import { Button, Text } from '@perch-labs/shrike';
import { useSessionContext } from 'contexts/session';
import { copyFor } from 'config';
import HomeEnergyIcon from '/images/icons/home-energy.svg';
import { Card } from 'styles';
import { useProductStatus, ProductType } from 'hooks/use-product-status.hook';
import {
  useFunnelRedirect,
  RedirectReason,
} from 'hooks/use-funnel-redirect.hook';
import { ErrorModule } from 'components/errors';
import { LoadingModule } from 'components/loading';
import { HighlightedCopyWrapper } from 'components/modules/ctas/ctas.style';
import { GET_UTILITY_CONNECT_CTA_QUERY } from './utility-connect-cta.api';

const getCopy = copyFor('cta.utilityConnect');

export const UtilityConnectCta = () => {
  const { userId } = useSessionContext();
  const { planId } = useParams<{ planId?: string }>();
  const connectedWind = useProductStatus(
    ProductType.ConnectedWind,
    Number(planId)
  );

  const { data, loading, error } = useQuery<
    GetUtilityConnectCtaInfoQuery,
    GetUtilityConnectCtaInfoQueryVariables
  >(GET_UTILITY_CONNECT_CTA_QUERY, {
    skip: !planId,
    variables: { planId: Number(planId) || -1 },
  });

  const [redirect, { loading: redirectLoading }] = useFunnelRedirect({
    idParams: { planId: Number(planId) },
    userId,
    reason: RedirectReason.UtilityConnectAvailable,
  });

  if (loading || connectedWind.loading) {
    return <LoadingModule variant="cta" />;
  }

  if (error || connectedWind.error || !connectedWind.data || !data?.plan) {
    return <ErrorModule />;
  }

  const mostRecentCleanKwh = data?.plan.cleanKilowattHours[0];
  const utilitySyncUnavailable = connectedWind.data.isUnavailable;
  const verificationStatus =
    data?.plan.utilityCredentials[0]?.verificationStatus;
  const nonEmptyCredsStatus =
    verificationStatus && verificationStatus !== 'empty';

  const hideComponent = nonEmptyCredsStatus || utilitySyncUnavailable;
  if (hideComponent) return null;

  const utility =
    data?.plan.utilityAccounts[0]?.utility?.displayName ||
    getCopy('utilityFallback');
  const mostRecentKwh = mostRecentCleanKwh?.kwh;

  return (
    <Card
      margin={{ bottom: '24px' }}
      small={{ margin: { bottom: '24px', left: '16px', right: '16px' } }}
    >
      <Card.Header>
        <Text textStyle="heading700" tag="h2" margin={{ right: '16px' }}>
          {getCopy('title', { utility })}
        </Text>
        <SVG
          aria-hidden="true"
          height="28px"
          width="28px"
          src={HomeEnergyIcon}
        />
      </Card.Header>
      <Card.Content>
        <HighlightedCopyWrapper background="blue">
          <Text opacity="medium">
            {getCopy('highlightedText', { kwh: mostRecentKwh, utility })}
          </Text>
        </HighlightedCopyWrapper>
        <Text margin={{ bottom: '16px' }} opacity="medium">
          {getCopy('text', { utility })}
        </Text>
        <Button
          loading={redirectLoading}
          onClick={redirect as (e: React.MouseEvent<HTMLButtonElement>) => void}
          fullWidth
        >
          {getCopy('button')}
        </Button>
      </Card.Content>
    </Card>
  );
};
